import { useState } from 'react';
import { toast } from 'sonner';
import { STORAGE_KEY } from '../constants';
import { RoleAccessConfig } from '../types';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

export function useRoleAccessActions(
  accessConfig: RoleAccessConfig,
  setAccessConfig: (config: RoleAccessConfig) => void
) {
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();

  // Save configuration to Supabase
  const handleSave = async () => {
    setIsSaving(true);

    try {
      console.log('Saving role access config to Supabase:', accessConfig);

      // Prepare upsert data
      const upsertData = Object.entries(accessConfig).map(([role, allowed_paths]) => ({
        role,
        allowed_paths,
        updated_at: new Date().toISOString()
      }));

      const { error } = await (supabase as any)
        .from('role_access_configs')
        .upsert(upsertData);

      if (error) throw error;

      // Also update localStorage for backward compatibility/immediate local fallback
      localStorage.setItem(STORAGE_KEY, JSON.stringify(accessConfig));

      // Invalidate the query to refresh sidebar and other components
      await queryClient.invalidateQueries({ queryKey: ['role-access-configs'] });

      toast.success('Role access configuration saved successfully to database');

      // Force reload to apply changes globally across all components if needed
      // (invalidateQueries might not be enough for some layout side-effects)
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error saving role access config:', error);
      toast.error('Failed to save role access configuration');
    } finally {
      setIsSaving(false);
    }
  };

  // Reset to default configuration (constants)
  const handleReset = async () => {
    // This just resets the local state in the table; user still needs to click Save to persist
    const { DEFAULT_ACCESS_CONFIG } = await import('../constants');
    setAccessConfig(DEFAULT_ACCESS_CONFIG);
    toast.info('Role access configuration reset to defaults in view. Click Save to persist.');
  };

  // Export configuration as JSON
  const handleExport = () => {
    try {
      const dataStr = JSON.stringify(accessConfig, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

      const exportLink = document.createElement('a');
      exportLink.setAttribute('href', dataUri);
      exportLink.setAttribute('download', 'role-access-config.json');
      exportLink.click();

      toast.success('Role access configuration exported');
    } catch (error) {
      console.error('Error exporting config:', error);
      toast.error('Failed to export configuration');
    }
  };

  // Import configuration from JSON
  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';

    input.onchange = (e: any) => {
      const file = e.target.files[0];
      const reader = new FileReader();

      reader.readAsText(file, 'UTF-8');
      reader.onload = readerEvent => {
        try {
          const content = readerEvent.target?.result as string;
          const parsedConfig = JSON.parse(content);
          setAccessConfig(parsedConfig);
          toast.success('Role access configuration imported to view. Click Save to persist.');
        } catch (error) {
          console.error('Error importing config:', error);
          toast.error('Invalid configuration file');
        }
      };

      reader.onerror = () => {
        toast.error('Error reading file');
      };
    };

    input.click();
  };

  return {
    isSaving,
    handleSave,
    handleReset,
    handleExport,
    handleImport
  };
}
